import {
  Button,
  Tab,
  TabList,
  Text,
  Tooltip,
  tokens,
  type SelectTabEvent,
  type SelectTabData,
} from "@fluentui/react-components";
import {
  ArrowRotateClockwise20Regular,
  ArrowRotateCounterclockwise20Regular,
} from "@fluentui/react-icons";
import { useEffect, useState } from "react";

import { PageImage } from "@/features/review/PageImage";

interface Page {
  name?: string;
  filename?: string;
  [k: string]: unknown;
}

type ViewerTab = "pages" | "text";

function pageName(p: Page, idx: number): string {
  return (p.name ?? p.filename ?? `page_${idx + 1}`) as string;
}

export interface DocumentViewerProps {
  documentId: string;
  pages: Page[];
  textContent: string;
}

export function DocumentViewer({
  documentId,
  pages,
  textContent,
}: DocumentViewerProps) {
  const [tab, setTab] = useState<ViewerTab>(
    pages.length > 0 ? "pages" : "text",
  );
  const [active, setActive] = useState(0);
  // Unbounded so each click animates exactly ±90°; wrapping the angle to
  // [0, 270] makes the CSS transition interpolate the long way (e.g.
  // 270 → 0 spins 270° backward instead of forward).
  const [rotation, setRotation] = useState<number>(0);

  // Keyboard navigation between pages — only when Pages tab is active.
  useEffect(() => {
    if (tab !== "pages") return;
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLElement) {
        const t = e.target.tagName;
        if (t === "INPUT" || t === "TEXTAREA") return;
      }
      if (e.key === "ArrowDown" || e.key === "PageDown") {
        setActive((i) => Math.min(pages.length - 1, i + 1));
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        setActive((i) => Math.max(0, i - 1));
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tab, pages.length]);

  useEffect(() => {
    setActive(0);
    setRotation(0);
  }, [documentId]);

  useEffect(() => {
    setRotation(0);
  }, [active]);

  const rotate = (delta: 90 | -90) => {
    setRotation((r) => r + delta);
  };

  const onTab = (_e: SelectTabEvent, data: SelectTabData) => {
    setTab(data.value as ViewerTab);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        background: tokens.colorNeutralBackground1,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 12px",
          borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        }}
      >
        <TabList selectedValue={tab} onTabSelect={onTab} size="small">
          <Tab value="pages" disabled={pages.length === 0}>
            Pages ({pages.length})
          </Tab>
          <Tab value="text" disabled={!textContent}>
            Text
          </Tab>
        </TabList>
        <div style={{ flex: 1 }} />
        {tab === "pages" && pages.length > 0 ? (
          <>
            <Tooltip content="Rotate counter-clockwise" relationship="label">
              <Button
                size="small"
                appearance="subtle"
                icon={<ArrowRotateCounterclockwise20Regular />}
                onClick={() => rotate(-90)}
              />
            </Tooltip>
            <Tooltip content="Rotate clockwise" relationship="label">
              <Button
                size="small"
                appearance="subtle"
                icon={<ArrowRotateClockwise20Regular />}
                onClick={() => rotate(90)}
              />
            </Tooltip>
          </>
        ) : null}
      </div>

      {tab === "pages" ? (
        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "52px 1fr",
            minHeight: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              padding: 8,
              overflowY: "auto",
              borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
            }}
          >
            {pages.map((p, idx) => {
              const name = pageName(p, idx);
              const isActive = idx === active;
              return (
                <button
                  key={name + idx}
                  type="button"
                  onClick={() => setActive(idx)}
                  style={{
                    padding: 6,
                    border: `2px solid ${
                      isActive
                        ? tokens.colorBrandStroke1
                        : tokens.colorNeutralStroke2
                    }`,
                    borderRadius: 4,
                    background: tokens.colorNeutralBackground1,
                    cursor: "pointer",
                    fontSize: 11,
                    color: tokens.colorNeutralForeground2,
                  }}
                  aria-label={`Page ${idx + 1}`}
                  aria-current={isActive}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>
          <div
            style={{
              minHeight: 0,
              minWidth: 0,
              overflow: "hidden",
              display: "flex",
              background: tokens.colorNeutralBackground2,
            }}
          >
            <PageImage
              key={`${documentId}/${pageName(pages[active]!, active)}`}
              documentId={documentId}
              pageName={pageName(pages[active]!, active)}
              rotation={rotation}
            />
          </div>
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 16,
            background: tokens.colorNeutralBackground2,
          }}
        >
          {textContent ? (
            <pre
              style={{
                whiteSpace: "pre-wrap",
                fontFamily: "ui-monospace, monospace",
                fontSize: 13,
                margin: 0,
              }}
            >
              {textContent}
            </pre>
          ) : (
            <Text style={{ color: tokens.colorNeutralForeground2 }}>
              No extracted text for this document.
            </Text>
          )}
        </div>
      )}
    </div>
  );
}
